import { useEffect, useRef, useState } from "react";
import type { Device } from "../../../../../supabase/functions/sync-control/model";
import { hasRecoveryFile } from "../../../../../supabase/functions/sync-control/policy";
import { interfaceLocale, useTranslation } from "../../i18n";
import type { RecoveryDraft } from "./recoveryDraft";
import { syncMessage } from "./syncMessage";
import type { useSyncReview } from "./useSyncReview";

export function ConnectedDevices({ controller }: { controller: ReturnType<typeof useSyncReview> }) {
  const { t } = useTranslation("syncReview");
  const [selected, setSelected] = useState<Device | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState("");
  const [openPassword, setOpenPassword] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [prepared, setPrepared] = useState(false);
  const [verified, setVerified] = useState(false);
  const draft = useRef<RecoveryDraft | null>(null);
  const confirming = useRef<HTMLHeadingElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const operation = useRef(false);
  const devices = controller.status.summary?.devices.filter((d) => !d.revokedAt) ?? [];
  const root = controller.status.summary?.root;
  const needsRecovery = !!root && hasRecoveryFile(root, controller.status.summary?.devices ?? []);
  const disabled = working || controller.status.busy || !navigator.onLine;
  useEffect(() => () => draft.current?.dispose(), []);
  useEffect(() => {
    if (selected) confirming.current?.focus();
  }, [selected]);
  const clear = () => {
    draft.current?.dispose();
    draft.current = null;
    setSelected(null);
    setPassword("");
    setOpenPassword("");
    setFile(null);
    setPrepared(false);
    setVerified(false);
    trigger.current?.focus();
  };
  const perform = async (task: () => Promise<void>) => {
    if (operation.current) return;
    operation.current = true;
    setWorking(true);
    setError("");
    setMessage("");
    try {
      await task();
    } catch (e) {
      setError(t(syncMessage(e instanceof Error ? e.message : "")));
    } finally {
      operation.current = false;
      setWorking(false);
    }
  };
  return (
    <section className="sync-connected-devices" aria-labelledby="connected-devices-title">
      <div className="sync-devices-heading">
        <h2 id="connected-devices-title" tabIndex={-1} ref={heading}>
          {t("connectedDevices")}
        </h2>
        <button type="button" disabled={disabled} onClick={() => void perform(() => controller.inspectAccount())}>
          {t("refreshDevices")}
        </button>
      </div>
      <p className="sync-storage-hint">{t("connectedDevicesHint")}</p>
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      <ul aria-label={t("connectedDevices")}>
        {devices.map((device) => (
          <li key={device.id}>
            <div>
              <strong>{device.label}</strong>
              {device.id === controller.device.current?.deviceId && (
                <span className="sync-current-device">{t("currentDevice")}</span>
              )}
              <small>
                {t("deviceConnectedAt", {
                  date: new Intl.DateTimeFormat(interfaceLocale(), { dateStyle: "medium", timeStyle: "short" }).format(
                    device.createdAt,
                  ),
                })}{" "}
                · <code title={device.id}>{device.id.slice(0, 8)}</code>
              </small>
            </div>
            {device.id !== controller.device.current?.deviceId && (
              <button
                type="button"
                disabled={disabled}
                aria-label={t("disconnectNamedDevice", { name: device.label, id: device.id.slice(0, 8) })}
                onClick={(event) => {
                  trigger.current = event.currentTarget;
                  setError("");
                  setMessage("");
                  setSelected(device);
                }}
              >
                {t("disconnectDevice")}
              </button>
            )}
          </li>
        ))}
      </ul>
      {selected && (
        <section className="sync-disconnect-confirm" aria-labelledby="disconnect-device-title">
          <h3 id="disconnect-device-title" tabIndex={-1} ref={confirming}>
            {t("disconnectDeviceTitle", { name: selected.label, id: selected.id.slice(0, 8) })}
          </h3>
          <p>{t("disconnectDeviceHint")}</p>
          {needsRecovery && (
            <>
              <p>{t("disconnectRecoveryHint")}</p>
              {!prepared ? (
                <>
                  <label>
                    {t("password")}
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      disabled={working}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={disabled || [...password].length < 15}
                    onClick={() =>
                      void perform(async () => {
                        draft.current?.dispose();
                        draft.current = await controller.prepareManagement("revoke", password);
                        setPassword("");
                        setPrepared(true);
                        const url = URL.createObjectURL(new Blob([draft.current.file], { type: "application/json" }));
                        const anchor = document.createElement("a");
                        anchor.href = url;
                        anchor.download = `classroom-sync-${Date.now()}.classroomsyncrecovery`;
                        anchor.click();
                        setTimeout(() => URL.revokeObjectURL(url), 10000);
                      })
                    }
                  >
                    {t("prepare")}
                  </button>
                </>
              ) : (
                <>
                  <label>
                    {t("reselect")}
                    <input
                      type="file"
                      accept=".classroomsyncrecovery"
                      disabled={working}
                      onChange={(e) => {
                        setFile(e.target.files?.[0] ?? null);
                        setVerified(false);
                      }}
                    />
                  </label>
                  <label>
                    {t("openPassword")}
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={openPassword}
                      disabled={working}
                      onChange={(e) => {
                        setOpenPassword(e.target.value);
                        setVerified(false);
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={disabled || !file || !openPassword}
                    onClick={() =>
                      void perform(async () => {
                        setVerified(false);
                        try {
                          if (!file || file.size > 32768 || !draft.current) throw new Error("sync-recovery-format");
                          await draft.current.verify(await file.text(), openPassword);
                          setVerified(true);
                        } finally {
                          setOpenPassword("");
                        }
                      })
                    }
                  >
                    {t("verifyFile")}
                  </button>
                  {verified && <p role="status">{t("fileVerified")}</p>}
                </>
              )}
            </>
          )}
          <div className="sync-review-actions">
            <button
              type="button"
              className="danger-action"
              disabled={disabled || (needsRecovery && !verified)}
              onClick={() =>
                void perform(async () => {
                  await controller.disconnect(selected.id, draft.current ?? undefined);
                  await controller.inspectAccount();
                  clear();
                  setMessage(t("deviceDisconnected", { name: selected.label }));
                  heading.current?.focus();
                })
              }
            >
              {t(working ? "working" : "confirmDisconnectDevice")}
            </button>
            <button type="button" disabled={working} onClick={clear}>
              {t("cancel")}
            </button>
          </div>
        </section>
      )}
    </section>
  );
}
